import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import ApiRegistrations from '../../../api/ApiRegistrations'
import ApiLeagues from '../../../api/ApiLeagues'
import { isValidSkillLevel } from '../../../utils/validateSkillLevel'
import toastr from 'toastr'
import { track } from '../../../analytics'
import { withTimeout, getErrorMessage } from '../../../utils/apiHelpers'
import { getPartnerDisplayName } from './partnerDisplayName'

function ReviewStep({
  selectedCourt,
  selectedLeagueId,
  selectedSkillLevel,
  nominatedPartner,
  validatedAccessCode,
  leagues,
  primaryPlayer,
  setMyLeagues,
  onBack,
  onBackSteps,
  onGoToStep,
  onComplete,
  showProfileModal,
}) {
  const [registering, setRegistering] = useState(false)
  const [registrationError, setRegistrationError] = useState(null)

  const selectedLeague = useMemo(
    () =>
      selectedLeagueId
        ? leagues.find((l) => l.id === parseInt(selectedLeagueId, 10)) || null
        : null,
    [selectedLeagueId, leagues],
  )
  const isDoublesRegistration = selectedLeague?.league_type === 'Doubles'

  // Determine if this is a paid registration flow (payment already completed)
  const isPaidRegistration = useMemo(
    () =>
      selectedLeague && selectedLeague.entry_fee > 0 && !validatedAccessCode,
    [selectedLeague, validatedAccessCode],
  )
  const nominatedPartnerName = getPartnerDisplayName(nominatedPartner)
  const nominatedPartnerHistoryPath =
    nominatedPartner?.type === 'player' && nominatedPartner?.id
      ? `/players/${nominatedPartner.id}/history`
      : null

  // Prevent navigation away from page during registration
  useEffect(() => {
    if (!registering) return

    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = '' // Chrome requires returnValue to be set
      return '' // Some browsers use return value
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [registering])

  const handleContinue = async () => {
    // For paid registrations, just advance to complete step
    // Registration was already completed during payment
    const leagueIdNum = parseInt(selectedLeagueId, 10)

    try {
      setRegistering(true)
      setRegistrationError(null)

      // Set home court for paid registrations
      try {
        await withTimeout(
          ApiLeagues.setHomeCourt({
            league_id: leagueIdNum,
            player_id: primaryPlayer.id,
            court_id: selectedCourt.id,
          }),
          30000,
        )
      } catch (e) {
        console.error('Failed to set home court after payment', e)
        toastr.warning(
          "Registration successful, but we couldn't set your home court. You can set it later from your league page.",
          '',
          { timeOut: 8000 },
        )
      }

      // Track completion for paid flow
      // Step 5 in paid flow (LEVEL, SKILL_LEVEL, COURT, PAYMENT, REVIEW)
      try {
        track('registration_completed', {
          step: 5,
          step_name: 'review',
          success: true,
          league_id: leagueIdNum,
          has_access_code: false,
          payment_flow: 'paid',
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }

      setMyLeagues((prevLeagues) => [...prevLeagues, leagueIdNum])
      onComplete(selectedLeague?.name || 'the league', leagueIdNum)
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setRegistrationError(errorMessage)
      toastr.error(errorMessage)
      console.error('Paid registration completion error:', err)

      // Track error
      try {
        track('registration_error', {
          step: 5,
          step_name: 'review',
          error_type: 'paid_completion_failed',
          error_message: errorMessage,
          league_id: leagueIdNum,
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }

      // Track failed completion
      try {
        track('registration_completed', {
          step: 5,
          step_name: 'review',
          success: false,
          error_message: errorMessage,
          league_id: leagueIdNum,
          has_access_code: false,
          payment_flow: 'paid',
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }
    } finally {
      setRegistering(false)
    }
  }

  const handleRegister = async () => {
    if (showProfileModal) {
      toastr.warning('Please complete your profile to register for a league.')
      return
    }

    if (!selectedLeagueId || !primaryPlayer?.id) {
      toastr.warning('Please select a league.')
      return
    }

    if (!selectedCourt?.id) {
      toastr.warning('Please select your home court.')
      return
    }

    if (!isValidSkillLevel(selectedSkillLevel)) {
      toastr.warning('Please select your skill level.')
      return
    }

    const leagueIdNum = parseInt(selectedLeagueId, 10)

    try {
      setRegistering(true)
      setRegistrationError(null)

      const registrationPayload = {
        player_id: primaryPlayer.id,
        league_id: leagueIdNum,
        skill_level: selectedSkillLevel,
        access_code: validatedAccessCode || undefined,
        ...(isDoublesRegistration && nominatedPartner?.type === 'player'
          ? { partner_player_id: nominatedPartner.id }
          : {}),
      }

      await withTimeout(
        ApiRegistrations.registerPlayer(registrationPayload),
        30000,
      )

      try {
        await withTimeout(
          ApiLeagues.setHomeCourt({
            league_id: leagueIdNum,
            player_id: primaryPlayer.id,
            court_id: selectedCourt.id,
          }),
          30000,
        )
      } catch (e) {
        console.error('Failed to set home court after registration', e)
        toastr.warning(
          "Registration successful, but we couldn't set your home court. You can set it later from your league page.",
          '',
          { timeOut: 8000 },
        )
      }

      // Track league registration with new method identifier for multi-step flow
      // Changed from 'form_submission' to differentiate new guided flow from legacy single-page registration
      try {
        track('league_joined', {
          league_id: leagueIdNum,
          player_id: primaryPlayer.id,
          method: 'multi_step_registration',
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }

      // Track successful registration completion
      try {
        track('registration_completed', {
          step: 4,
          step_name: 'review',
          success: true,
          league_id: leagueIdNum,
          has_access_code: !!validatedAccessCode,
          payment_flow: 'free',
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }

      setMyLeagues((prevLeagues) => [...prevLeagues, leagueIdNum])
      onComplete(selectedLeague?.name || 'the league', leagueIdNum)
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setRegistrationError(errorMessage)
      toastr.error(errorMessage)
      console.error('Registration error:', err)

      // Track registration error
      try {
        track('registration_error', {
          step: 4,
          step_name: 'review',
          error_type: 'registration_failed',
          error_message: errorMessage,
          league_id: leagueIdNum,
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }

      // Track failed registration completion
      try {
        track('registration_completed', {
          step: 4,
          step_name: 'review',
          success: false,
          error_message: errorMessage,
          league_id: leagueIdNum,
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }
    } finally {
      setRegistering(false)
    }
  }

  if (
    !selectedLeague ||
    !selectedCourt ||
    !isValidSkillLevel(selectedSkillLevel)
  ) {
    return (
      <div className="registration-step">
        <p>
          Missing required information. Please go back and complete all steps.
        </p>
        <button className="btn btn-outline-secondary" onClick={onBack}>
          Back
        </button>
      </div>
    )
  }

  const handleChangeStep = (targetStep) => {
    // Prefer onGoToStep for direct step navigation (avoids offset bugs)
    if (typeof onGoToStep === 'function') {
      try {
        onGoToStep(targetStep)
      } catch (error) {
        console.error('Error navigating to step:', error)
        toastr.error('Unable to navigate back. Please try again.')
      }
      return
    }

    // Fallback to onBackSteps with offset (legacy)
    if (typeof onBackSteps === 'function') {
      try {
        onBackSteps(targetStep)
      } catch (error) {
        console.error('Error navigating back:', error)
        toastr.error('Unable to navigate back. Please try again.')
      }
      return
    }

    // Last-resort fallback
    if (typeof onBack === 'function') {
      try {
        onBack()
      } catch (error) {
        console.error('Error navigating back:', error)
        toastr.error('Unable to navigate back. Please try again.')
      }
    }
  }

  return (
    <div className="registration-step">
      <h4 className="step-title">
        Step {isPaidRegistration ? '5' : '4'}: Review & Confirm
      </h4>
      <p className="step-description">
        {isPaidRegistration
          ? 'Your payment has been processed and registration is complete.'
          : 'Please review your selections before completing registration.'}
      </p>

      <div className="review-section">
        <div className="review-item">
          <div className="review-item-header">
            <h5>League</h5>
            <button
              className="review-change-link"
              onClick={() => handleChangeStep(1)}
              disabled={registering}
              aria-label="Change league selection"
            >
              Change
            </button>
          </div>
          <p className="review-value">
            {selectedLeague.name} • {selectedLeague.league_type}
          </p>
        </div>

        <div className="review-item">
          <div className="review-item-header">
            <h5>Skill Level</h5>
            <button
              className="review-change-link"
              onClick={() => handleChangeStep(2)}
              disabled={registering}
              aria-label="Change skill level"
            >
              Change
            </button>
          </div>
          <p className="review-value">{selectedSkillLevel}</p>
        </div>

        {isDoublesRegistration && (
          <div className="review-item">
            <div className="review-item-header">
              <h5>Partner</h5>
              {!isPaidRegistration && (
                <button
                  className="review-change-link"
                  onClick={() => handleChangeStep(3)}
                  disabled={registering}
                  aria-label="Change doubles partner (goes back to court step)"
                >
                  Change
                </button>
              )}
            </div>
            <div className="review-value">
              {nominatedPartnerName && (
                <p className="mb-0">{nominatedPartnerName}</p>
              )}
              {nominatedPartnerHistoryPath && (
                <Link
                  className="review-change-link d-inline-block mt-2 px-0 py-0"
                  to={nominatedPartnerHistoryPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={
                    nominatedPartnerName
                      ? `View player page for ${nominatedPartnerName}`
                      : 'View selected partner player page'
                  }
                >
                  View player page
                </Link>
              )}
              {isPaidRegistration && (
                <small className="text-muted d-block mt-1">
                  Partner selection is locked after payment.
                </small>
              )}
            </div>
          </div>
        )}

        <div className="review-item">
          <div className="review-item-header">
            <h5>Home Court</h5>
            <button
              className="review-change-link"
              onClick={() => handleChangeStep(3)}
              disabled={registering}
              aria-label="Change home court"
            >
              Change
            </button>
          </div>
          <p className="review-value">
            {selectedCourt.name}
            {selectedCourt.address_line1 && ` • ${selectedCourt.address_line1}`}
            {selectedCourt.city && `, ${selectedCourt.city}`}
            {selectedCourt.state && `, ${selectedCourt.state}`}
          </p>
        </div>

        {/* Price section - no Change link because price is determined by league selection
            and cannot be modified independently. To change price, user must change league. */}
        <div className="review-item review-price">
          <h5>Price</h5>
          <p className="review-value review-price-value">
            {isPaidRegistration ? (
              <>
                ${selectedLeague.entry_fee.toFixed(2)}
                <span className="text-success ms-2">(Payment complete ✓)</span>
              </>
            ) : (
              <>
                Free Registration
                {validatedAccessCode && (
                  <span className="text-success ms-2">
                    (Access code applied ✓)
                  </span>
                )}
              </>
            )}
          </p>
        </div>
      </div>

      {registrationError && (
        <div
          className="alert alert-danger mt-3"
          role="alert"
          aria-live="assertive"
        >
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <strong>Registration Failed:</strong> {registrationError}
            </div>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => {
                setRegistrationError(null)
                handleRegister()
              }}
              disabled={registering}
              aria-label="Retry registration"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="step-navigation">
        <button
          className="btn btn-outline-secondary"
          onClick={onBack}
          disabled={registering}
          aria-label={
            isPaidRegistration
              ? 'Go back to payment step'
              : 'Go back to court selection'
          }
        >
          Back
        </button>
        {isPaidRegistration ? (
          <button
            className="btn btn-primary btn-lg flex-grow-1"
            onClick={handleContinue}
            disabled={registering}
            aria-label={
              registering
                ? 'Processing completion'
                : 'Continue to complete registration'
            }
          >
            {registering ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                />
                Processing...
              </>
            ) : (
              'Continue'
            )}
          </button>
        ) : (
          <button
            className="btn btn-primary btn-lg flex-grow-1"
            onClick={handleRegister}
            disabled={registering || showProfileModal}
            aria-label={
              registering
                ? 'Registration in progress'
                : validatedAccessCode
                  ? 'Complete free registration'
                  : 'Confirm and register for league'
            }
          >
            {registering ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                />
                Registering...
              </>
            ) : validatedAccessCode ? (
              'Complete Registration'
            ) : (
              'Confirm & Register'
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export default ReviewStep
